/**
 * Extended UI translation keys, added in iteration 28 to close the
 * 100%-coverage audit (Task 1). These get merged into the master
 * `translations` object at module load so the existing `t()` helper picks
 * them up unchanged.
 *
 * Naming convention: snake_case key, prefixed by the surface where it
 * appears (e.g. `blog_*`, `auth_*`, `profile_*`). Add NEW strings here
 * rather than editing the master file, keeps merges atomic.
 */
export const extraTranslations = {
  en: {
    // Blog
    blog_scientific_insights: 'Scientific Insights',
    blog_more_articles_coming: 'More articles coming soon...',
    blog_article_not_found: 'Article Not Found',
    blog_article_not_found_desc: 'The article you are looking for could not be found.',
    blog_back_to_blog: 'Back to Blog',
    blog_label: 'Blog Article',
    blog_last_updated: 'Updated',
    blog_last_updated_tooltip: 'Last revised',

    // Auth / address
    address_apartment_suite: 'Apartment, suite, etc.',
    address_street_address: 'Street address',
    auth_research_institute_placeholder: 'Research Institute of...',

    // Profile
    profile_account_settings: 'Account Settings',
    profile_manage_subtitle: 'Manage your account information and preferences',

    // Cart / Checkout
    cart_review_subtitle: 'Review your items and proceed to checkout',
    checkout_processing: 'Processing...',
    checkout_success_please_wait: 'Please wait while we confirm your payment...',

    // Orders
    orders_title: 'Your Orders',
    orders_subtitle: 'Track your orders and view your purchase history',

    // Affiliates / generic
    affiliates_submitting: 'Submitting...',

    // Recommendations
    rec_ai_powered: 'AI-Powered',
    rec_choose_goal: 'Choose a goal above to see personalized recommendations',
    rec_showing_for: 'Showing products for:',
    rec_section_title: 'Recommendations',
    rec_scroll_left: 'Scroll left',
    rec_scroll_right: 'Scroll right',

    // Phone input
    phone_search_countries: 'Search countries...',
    phone_no_countries: 'No countries found',

    // Product page
    product_not_found: 'Product Not Found',
    product_target_organ: 'Target organ',
    product_licensed_only: 'Licensed Professionals Only:',
    product_no_image_available: 'No image available',

    // Pages
    about_our_company: 'Our Company',
    about_principles: 'PRINCIPLES',
    peptides_research_compounds: 'RESEARCH COMPOUNDS',
    peptides_sarms_research: 'SARMs Research',
    peptides_scientific_research: 'Scientific Research',
    distributor_advantages: 'Our Advantages',
    distributor_partnership: 'Partnership Program',
    distributor_requirements: 'Requirements',
    distributor_start_today: 'Start Today',
    contact_background_alt: 'Contact Background',
    technology_background_alt: 'Technology Background',
    calc_research_tool: 'Research Tool',

    // Footer
    footer_system_status: 'System Status',
    footer_affiliates: 'Affiliates',
    badge_new: 'NEW',
  },

  es: {
    blog_scientific_insights: 'Perspectivas científicas',
    blog_more_articles_coming: 'Más artículos próximamente...',
    blog_article_not_found: 'Artículo no encontrado',
    blog_article_not_found_desc: 'El artículo que busca no se ha encontrado.',
    blog_back_to_blog: 'Volver al blog',
    blog_label: 'Artículo de blog',
    blog_last_updated: 'Actualizado',
    blog_last_updated_tooltip: 'Última revisión',

    address_apartment_suite: 'Apartamento, piso, etc.',
    address_street_address: 'Dirección postal',
    auth_research_institute_placeholder: 'Instituto de Investigación de...',

    profile_account_settings: 'Ajustes de la cuenta',
    profile_manage_subtitle: 'Gestiona la información y preferencias de tu cuenta',

    cart_review_subtitle: 'Revisa tus artículos y procede al pago',
    checkout_processing: 'Procesando...',
    checkout_success_please_wait: 'Por favor, espera mientras confirmamos tu pago...',

    orders_title: 'Tus pedidos',
    orders_subtitle: 'Sigue tus pedidos y consulta tu historial de compras',

    affiliates_submitting: 'Enviando...',

    rec_ai_powered: 'Con IA',
    rec_choose_goal: 'Elige un objetivo arriba para ver recomendaciones personalizadas',
    rec_showing_for: 'Mostrando productos para:',
    rec_section_title: 'Recomendaciones',
    rec_scroll_left: 'Desplazar a la izquierda',
    rec_scroll_right: 'Desplazar a la derecha',

    phone_search_countries: 'Buscar países...',
    phone_no_countries: 'No se han encontrado países',

    product_not_found: 'Producto no encontrado',
    product_target_organ: 'Órgano objetivo',
    product_licensed_only: 'Solo profesionales autorizados:',
    product_no_image_available: 'Imagen no disponible',

    about_our_company: 'Nuestra empresa',
    about_principles: 'PRINCIPIOS',
    peptides_research_compounds: 'COMPUESTOS DE INVESTIGACIÓN',
    peptides_sarms_research: 'Investigación de SARMs',
    peptides_scientific_research: 'Investigación científica',
    distributor_advantages: 'Nuestras ventajas',
    distributor_partnership: 'Programa de asociación',
    distributor_requirements: 'Requisitos',
    distributor_start_today: 'Empieza hoy',
    contact_background_alt: 'Imagen de fondo de contacto',
    technology_background_alt: 'Imagen de fondo de tecnología',
    calc_research_tool: 'Herramienta de investigación',

    footer_system_status: 'Estado del sistema',
    footer_affiliates: 'Afiliados',
    badge_new: 'NUEVO',
  },

  ru: {
    blog_scientific_insights: 'Научные обзоры',
    blog_more_articles_coming: 'Скоро новые статьи...',
    blog_article_not_found: 'Статья не найдена',
    blog_article_not_found_desc: 'Запрашиваемая статья не найдена.',
    blog_back_to_blog: 'Назад в блог',
    blog_label: 'Статья блога',
    blog_last_updated: 'Обновлено',
    blog_last_updated_tooltip: 'Последняя редакция',

    address_apartment_suite: 'Квартира, корпус и т. д.',
    address_street_address: 'Улица и дом',
    auth_research_institute_placeholder: 'Исследовательский институт...',

    profile_account_settings: 'Настройки аккаунта',
    profile_manage_subtitle: 'Управляйте данными и настройками вашего аккаунта',

    cart_review_subtitle: 'Проверьте товары и перейдите к оформлению',
    checkout_processing: 'Обработка...',
    checkout_success_please_wait: 'Пожалуйста, подождите, мы подтверждаем ваш платёж...',

    orders_title: 'Ваши заказы',
    orders_subtitle: 'Отслеживайте заказы и просматривайте историю покупок',

    affiliates_submitting: 'Отправка...',

    rec_ai_powered: 'С ИИ',
    rec_choose_goal: 'Выберите цель выше, чтобы увидеть персональные рекомендации',
    rec_showing_for: 'Показаны товары для:',
    rec_section_title: 'Рекомендации',
    rec_scroll_left: 'Прокрутить влево',
    rec_scroll_right: 'Прокрутить вправо',

    phone_search_countries: 'Поиск стран...',
    phone_no_countries: 'Страны не найдены',

    product_not_found: 'Товар не найден',
    product_target_organ: 'Целевой орган',
    product_licensed_only: 'Только для лицензированных специалистов:',
    product_no_image_available: 'Изображение недоступно',

    about_our_company: 'Наша компания',
    about_principles: 'ПРИНЦИПЫ',
    peptides_research_compounds: 'ИССЛЕДОВАТЕЛЬСКИЕ СОЕДИНЕНИЯ',
    peptides_sarms_research: 'Исследования SARM',
    peptides_scientific_research: 'Научные исследования',
    distributor_advantages: 'Наши преимущества',
    distributor_partnership: 'Партнёрская программа',
    distributor_requirements: 'Требования',
    distributor_start_today: 'Начните сегодня',
    contact_background_alt: 'Фон страницы контактов',
    technology_background_alt: 'Фон страницы технологий',
    calc_research_tool: 'Инструмент исследования',

    footer_system_status: 'Статус системы',
    footer_affiliates: 'Партнёрская программа',
    badge_new: 'НОВОЕ',
  },

  de: {
    blog_scientific_insights: 'Wissenschaftliche Einblicke',
    blog_more_articles_coming: 'Bald weitere Artikel...',
    blog_article_not_found: 'Artikel nicht gefunden',
    blog_article_not_found_desc: 'Der gesuchte Artikel konnte nicht gefunden werden.',
    blog_back_to_blog: 'Zurück zum Blog',
    blog_label: 'Blog-Artikel',
    blog_last_updated: 'Aktualisiert',
    blog_last_updated_tooltip: 'Zuletzt überarbeitet',

    address_apartment_suite: 'Wohnung, Etage usw.',
    address_street_address: 'Straße und Hausnummer',
    auth_research_institute_placeholder: 'Forschungsinstitut für...',

    profile_account_settings: 'Kontoeinstellungen',
    profile_manage_subtitle: 'Kontodaten und Einstellungen verwalten',

    cart_review_subtitle: 'Artikel überprüfen und zur Kasse gehen',
    checkout_processing: 'Verarbeitung...',
    checkout_success_please_wait: 'Bitte warten, wir bestätigen Ihre Zahlung...',

    orders_title: 'Ihre Bestellungen',
    orders_subtitle: 'Bestellungen verfolgen und Bestellverlauf einsehen',

    affiliates_submitting: 'Wird gesendet...',

    rec_ai_powered: 'KI-gestützt',
    rec_choose_goal: 'Wählen Sie oben ein Ziel, um persönliche Empfehlungen zu sehen',
    rec_showing_for: 'Produkte angezeigt für:',
    rec_section_title: 'Empfehlungen',
    rec_scroll_left: 'Nach links scrollen',
    rec_scroll_right: 'Nach rechts scrollen',

    phone_search_countries: 'Länder suchen...',
    phone_no_countries: 'Keine Länder gefunden',

    product_not_found: 'Produkt nicht gefunden',
    product_target_organ: 'Zielorgan',
    product_licensed_only: 'Nur für lizenzierte Fachkräfte:',
    product_no_image_available: 'Kein Bild verfügbar',

    about_our_company: 'Unser Unternehmen',
    about_principles: 'PRINZIPIEN',
    peptides_research_compounds: 'FORSCHUNGSVERBINDUNGEN',
    peptides_sarms_research: 'SARM-Forschung',
    peptides_scientific_research: 'Wissenschaftliche Forschung',
    distributor_advantages: 'Unsere Vorteile',
    distributor_partnership: 'Partnerprogramm',
    distributor_requirements: 'Anforderungen',
    distributor_start_today: 'Heute starten',
    contact_background_alt: 'Hintergrundbild Kontakt',
    technology_background_alt: 'Hintergrundbild Technologie',
    calc_research_tool: 'Forschungswerkzeug',

    footer_system_status: 'Systemstatus',
    footer_affiliates: 'Affiliates',
    badge_new: 'NEU',
  },

  fr: {
    blog_scientific_insights: 'Aperçus scientifiques',
    blog_more_articles_coming: "D'autres articles bientôt...",
    blog_article_not_found: 'Article introuvable',
    blog_article_not_found_desc: "L'article que vous recherchez est introuvable.",
    blog_back_to_blog: 'Retour au blog',
    blog_label: 'Article de blog',
    blog_last_updated: 'Mis à jour',
    blog_last_updated_tooltip: 'Dernière révision',

    address_apartment_suite: 'Appartement, étage, etc.',
    address_street_address: 'Adresse postale',
    auth_research_institute_placeholder: 'Institut de Recherche de...',

    profile_account_settings: 'Paramètres du compte',
    profile_manage_subtitle: 'Gérez les informations et les préférences de votre compte',

    cart_review_subtitle: 'Vérifiez vos articles et passez à la caisse',
    checkout_processing: 'Traitement...',
    checkout_success_please_wait: 'Veuillez patienter, nous confirmons votre paiement...',

    orders_title: 'Vos commandes',
    orders_subtitle: "Suivez vos commandes et consultez l'historique d'achats",

    affiliates_submitting: 'Envoi en cours...',

    rec_ai_powered: 'Propulsé par IA',
    rec_choose_goal: 'Choisissez un objectif ci-dessus pour voir des recommandations personnalisées',
    rec_showing_for: 'Produits affichés pour :',
    rec_section_title: 'Recommandations',
    rec_scroll_left: 'Défiler à gauche',
    rec_scroll_right: 'Défiler à droite',

    phone_search_countries: 'Rechercher des pays...',
    phone_no_countries: 'Aucun pays trouvé',

    product_not_found: 'Produit introuvable',
    product_target_organ: 'Organe cible',
    product_licensed_only: 'Réservé aux professionnels agréés :',
    product_no_image_available: 'Image non disponible',

    about_our_company: 'Notre entreprise',
    about_principles: 'PRINCIPES',
    peptides_research_compounds: 'COMPOSÉS DE RECHERCHE',
    peptides_sarms_research: 'Recherche sur les SARM',
    peptides_scientific_research: 'Recherche scientifique',
    distributor_advantages: 'Nos avantages',
    distributor_partnership: 'Programme de partenariat',
    distributor_requirements: 'Exigences',
    distributor_start_today: "Commencer aujourd'hui",
    contact_background_alt: "Arrière-plan de la page de contact",
    technology_background_alt: 'Arrière-plan de la page technologie',
    calc_research_tool: 'Outil de recherche',

    footer_system_status: 'État du système',
    footer_affiliates: 'Affiliés',
    badge_new: 'NOUVEAU',
  },

  it: {
    blog_scientific_insights: 'Approfondimenti scientifici',
    blog_more_articles_coming: 'Altri articoli in arrivo...',
    blog_article_not_found: 'Articolo non trovato',
    blog_article_not_found_desc: 'Impossibile trovare l\'articolo richiesto.',
    blog_back_to_blog: 'Torna al blog',
    blog_label: 'Articolo del blog',
    blog_last_updated: 'Aggiornato',
    blog_last_updated_tooltip: 'Ultima revisione',

    address_apartment_suite: 'Appartamento, piano, ecc.',
    address_street_address: 'Indirizzo (via e numero civico)',
    auth_research_institute_placeholder: 'Istituto di Ricerca di...',

    profile_account_settings: 'Impostazioni account',
    profile_manage_subtitle: 'Gestisci le informazioni e le preferenze del tuo account',

    cart_review_subtitle: 'Controlla gli articoli e procedi al checkout',
    checkout_processing: 'Elaborazione...',
    checkout_success_please_wait: 'Attendi mentre confermiamo il pagamento...',

    orders_title: 'I tuoi ordini',
    orders_subtitle: 'Monitora i tuoi ordini e visualizza la cronologia acquisti',

    affiliates_submitting: 'Invio in corso...',

    rec_ai_powered: 'Basato su IA',
    rec_choose_goal: 'Scegli un obiettivo sopra per vedere consigli personalizzati',
    rec_showing_for: 'Prodotti per:',
    rec_section_title: 'Consigli',
    rec_scroll_left: 'Scorri a sinistra',
    rec_scroll_right: 'Scorri a destra',

    phone_search_countries: 'Cerca paesi...',
    phone_no_countries: 'Nessun paese trovato',

    product_not_found: 'Prodotto non trovato',
    product_target_organ: 'Organo bersaglio',
    product_licensed_only: 'Solo per professionisti autorizzati:',
    product_no_image_available: 'Immagine non disponibile',

    about_our_company: 'La nostra azienda',
    about_principles: 'PRINCIPI',
    peptides_research_compounds: 'COMPOSTI DA RICERCA',
    peptides_sarms_research: 'Ricerca SARM',
    peptides_scientific_research: 'Ricerca scientifica',
    distributor_advantages: 'I nostri vantaggi',
    distributor_partnership: 'Programma di partnership',
    distributor_requirements: 'Requisiti',
    distributor_start_today: 'Inizia oggi',
    contact_background_alt: 'Sfondo pagina contatti',
    technology_background_alt: 'Sfondo pagina tecnologia',
    calc_research_tool: 'Strumento di ricerca',

    footer_system_status: 'Stato del sistema',
    footer_affiliates: 'Affiliati',
    badge_new: 'NUOVO',
  },

  pl: {
    blog_scientific_insights: 'Wgląd naukowy',
    blog_more_articles_coming: 'Wkrótce więcej artykułów...',
    blog_article_not_found: 'Nie znaleziono artykułu',
    blog_article_not_found_desc: 'Szukany artykuł nie został znaleziony.',
    blog_back_to_blog: 'Powrót do bloga',
    blog_label: 'Artykuł blogowy',
    blog_last_updated: 'Zaktualizowano',
    blog_last_updated_tooltip: 'Ostatnia rewizja',

    address_apartment_suite: 'Mieszkanie, lokal, itp.',
    address_street_address: 'Ulica i numer',
    auth_research_institute_placeholder: 'Instytut Badawczy...',

    profile_account_settings: 'Ustawienia konta',
    profile_manage_subtitle: 'Zarządzaj danymi i preferencjami konta',

    cart_review_subtitle: 'Sprawdź produkty i przejdź do kasy',
    checkout_processing: 'Przetwarzanie...',
    checkout_success_please_wait: 'Proszę czekać, potwierdzamy Twoją płatność...',

    orders_title: 'Twoje zamówienia',
    orders_subtitle: 'Śledź zamówienia i zobacz historię zakupów',

    affiliates_submitting: 'Wysyłanie...',

    rec_ai_powered: 'Wspomagane SI',
    rec_choose_goal: 'Wybierz cel powyżej, aby zobaczyć spersonalizowane rekomendacje',
    rec_showing_for: 'Pokazuję produkty dla:',
    rec_section_title: 'Rekomendacje',
    rec_scroll_left: 'Przewiń w lewo',
    rec_scroll_right: 'Przewiń w prawo',

    phone_search_countries: 'Szukaj krajów...',
    phone_no_countries: 'Nie znaleziono krajów',

    product_not_found: 'Nie znaleziono produktu',
    product_target_organ: 'Narząd docelowy',
    product_licensed_only: 'Tylko dla licencjonowanych specjalistów:',
    product_no_image_available: 'Brak dostępnego obrazu',

    about_our_company: 'Nasza firma',
    about_principles: 'ZASADY',
    peptides_research_compounds: 'ZWIĄZKI BADAWCZE',
    peptides_sarms_research: 'Badania nad SARM',
    peptides_scientific_research: 'Badania naukowe',
    distributor_advantages: 'Nasze atuty',
    distributor_partnership: 'Program partnerski',
    distributor_requirements: 'Wymagania',
    distributor_start_today: 'Zacznij dziś',
    contact_background_alt: 'Tło strony kontakt',
    technology_background_alt: 'Tło strony technologia',
    calc_research_tool: 'Narzędzie badawcze',

    footer_system_status: 'Status systemu',
    footer_affiliates: 'Program partnerski',
    badge_new: 'NOWOŚĆ',
  },

  nl: {
    blog_scientific_insights: 'Wetenschappelijke inzichten',
    blog_more_articles_coming: 'Binnenkort meer artikelen...',
    blog_article_not_found: 'Artikel niet gevonden',
    blog_article_not_found_desc: 'Het artikel dat u zoekt, kan niet worden gevonden.',
    blog_back_to_blog: 'Terug naar blog',
    blog_label: 'Blogartikel',
    blog_last_updated: 'Bijgewerkt',
    blog_last_updated_tooltip: 'Laatst herzien',

    address_apartment_suite: 'Appartement, etage, etc.',
    address_street_address: 'Straat en huisnummer',
    auth_research_institute_placeholder: 'Onderzoeksinstituut voor...',

    profile_account_settings: 'Accountinstellingen',
    profile_manage_subtitle: 'Beheer uw accountgegevens en voorkeuren',

    cart_review_subtitle: 'Controleer uw artikelen en ga door naar afrekenen',
    checkout_processing: 'Verwerken...',
    checkout_success_please_wait: 'Even geduld, we bevestigen uw betaling...',

    orders_title: 'Uw bestellingen',
    orders_subtitle: 'Volg uw bestellingen en bekijk uw aankoopgeschiedenis',

    affiliates_submitting: 'Versturen...',

    rec_ai_powered: 'AI-gestuurd',
    rec_choose_goal: 'Kies hierboven een doel om persoonlijke aanbevelingen te zien',
    rec_showing_for: 'Producten voor:',
    rec_section_title: 'Aanbevelingen',
    rec_scroll_left: 'Naar links scrollen',
    rec_scroll_right: 'Naar rechts scrollen',

    phone_search_countries: 'Landen zoeken...',
    phone_no_countries: 'Geen landen gevonden',

    product_not_found: 'Product niet gevonden',
    product_target_organ: 'Doelorgaan',
    product_licensed_only: 'Alleen voor gelicentieerde professionals:',
    product_no_image_available: 'Geen afbeelding beschikbaar',

    about_our_company: 'Ons bedrijf',
    about_principles: 'PRINCIPES',
    peptides_research_compounds: 'ONDERZOEKSVERBINDINGEN',
    peptides_sarms_research: 'SARM-onderzoek',
    peptides_scientific_research: 'Wetenschappelijk onderzoek',
    distributor_advantages: 'Onze voordelen',
    distributor_partnership: 'Partnerprogramma',
    distributor_requirements: 'Vereisten',
    distributor_start_today: 'Begin vandaag',
    contact_background_alt: 'Achtergrondafbeelding contactpagina',
    technology_background_alt: 'Achtergrondafbeelding technologiepagina',
    calc_research_tool: 'Onderzoekstool',

    footer_system_status: 'Systeemstatus',
    footer_affiliates: 'Affiliates',
    badge_new: 'NIEUW',
  },

  pt: {
    blog_scientific_insights: 'Insights científicos',
    blog_more_articles_coming: 'Mais artigos em breve...',
    blog_article_not_found: 'Artigo não encontrado',
    blog_article_not_found_desc: 'O artigo que você procura não foi encontrado.',
    blog_back_to_blog: 'Voltar ao blog',
    blog_label: 'Artigo do blog',
    blog_last_updated: 'Atualizado',
    blog_last_updated_tooltip: 'Última revisão',

    address_apartment_suite: 'Apartamento, andar, etc.',
    address_street_address: 'Endereço (rua e número)',
    auth_research_institute_placeholder: 'Instituto de Pesquisa de...',

    profile_account_settings: 'Configurações da conta',
    profile_manage_subtitle: 'Gerencie as informações e preferências da sua conta',

    cart_review_subtitle: 'Revise os itens e prossiga para o pagamento',
    checkout_processing: 'Processando...',
    checkout_success_please_wait: 'Aguarde enquanto confirmamos seu pagamento...',

    orders_title: 'Seus pedidos',
    orders_subtitle: 'Acompanhe seus pedidos e veja seu histórico de compras',

    affiliates_submitting: 'Enviando...',

    rec_ai_powered: 'Com IA',
    rec_choose_goal: 'Escolha um objetivo acima para ver recomendações personalizadas',
    rec_showing_for: 'Mostrando produtos para:',
    rec_section_title: 'Recomendações',
    rec_scroll_left: 'Rolar para a esquerda',
    rec_scroll_right: 'Rolar para a direita',

    phone_search_countries: 'Buscar países...',
    phone_no_countries: 'Nenhum país encontrado',

    product_not_found: 'Produto não encontrado',
    product_target_organ: 'Órgão-alvo',
    product_licensed_only: 'Apenas profissionais licenciados:',
    product_no_image_available: 'Imagem indisponível',

    about_our_company: 'Nossa empresa',
    about_principles: 'PRINCÍPIOS',
    peptides_research_compounds: 'COMPOSTOS DE PESQUISA',
    peptides_sarms_research: 'Pesquisa em SARMs',
    peptides_scientific_research: 'Pesquisa científica',
    distributor_advantages: 'Nossas vantagens',
    distributor_partnership: 'Programa de parceria',
    distributor_requirements: 'Requisitos',
    distributor_start_today: 'Comece hoje',
    contact_background_alt: 'Plano de fundo da página de contato',
    technology_background_alt: 'Plano de fundo da página de tecnologia',
    calc_research_tool: 'Ferramenta de pesquisa',

    footer_system_status: 'Status do sistema',
    footer_affiliates: 'Afiliados',
    badge_new: 'NOVO',
  },
};

// Merge into the master translations object so the existing `t()` helper
// resolves these keys without any code changes elsewhere.
export function mergeExtraTranslations(translations) {
  for (const lang of Object.keys(extraTranslations)) {
    if (!translations[lang]) translations[lang] = {};
    Object.assign(translations[lang], extraTranslations[lang]);
  }
  return translations;
}
